import type {
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import React from "react";

import ABTesting from "@svg/billing/ABTesting";
import Analytics from "@svg/billing/Analytics";
import General from "@svg/billing/General";
import MostPopular from "@svg/billing/MostPopular";
import ReploBuilder from "@svg/billing/ReploBuilder";
import ShopifyThemeEditor from "@svg/billing/ShopifyThemeEditor";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { getProductLabels, getProjectLabels } from "@v2/utils/project-labels";
import { pluralize } from "replo-utils/lib/pluralize";
import {
  ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE,
  AddOn,
  MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES,
} from "schemas/addOns";
import { getBillingPlanInfoByTier } from "schemas/billing";

import { PlanChangeConfirmationModal } from "../modals/PlanChangeConfirmationModal";
import { EstimatedPlanPricePerMonth } from "../PlanPrice";
import { getPercentSavedBySwitchingToYearly } from "../planPriceUtils";
import PlanCardButton from "../shared/PlanCardButton";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "../shared/sharedBillingUtils";
import { PlanLabel } from "../SharedBillingComponents";
import getPlanCardCopy from "./getPlanCardCopy";
import QuantityControl from "./QuantityControl";

const PlanCard = ({
  position,
  cardTier,
  isYearly,
  onIsYearlyChange,
  activeSubscription,
  additionalSites,
  additionalProjects,
  additionalPublishedElementBundles,
  currentBillingTier,
  handleAdditionalSitesIncrement,
  handleAdditionalSitesDecrement,
  handleAdditionalProjectsIncrement,
  handleAdditionalProjectsDecrement,
  handleAdditionalPublishedElementBundlesIncrement,
  handleAdditionalPublishedElementBundlesDecrement,
}: {
  position: "left" | "right";
  cardTier: "starter" | "pro";
  isYearly: boolean;
  onIsYearlyChange: (isYearly: boolean) => void;
  activeSubscription?: SubscriptionDetails;
  additionalSites: number;
  additionalProjects: number;
  additionalPublishedElementBundles: number;
  currentBillingTier: BillingTier;
  handleAdditionalSitesIncrement: () => void;
  handleAdditionalSitesDecrement: () => void;
  handleAdditionalProjectsIncrement: () => void;
  handleAdditionalProjectsDecrement: () => void;
  handleAdditionalPublishedElementBundlesIncrement: () => void;
  handleAdditionalPublishedElementBundlesDecrement: () => void;
}) => {
  const [planChangeModalOpen, setPlanChangeModalOpen] = React.useState(false);

  const workspace = useCurrentWorkspace();

  const productLabels = getProductLabels();
  const reploSitesTitle = productLabels.reploBuilder;
  const shopifyThemeBuilderTitle = productLabels.shopifyThemeBuilder;

  const siteLabels = getProjectLabels("replo");
  const projectLabels = getProjectLabels("shopify");

  const { data: reploSitesUsage } = workspaceRouter.getReploSitesUsage.useQuery(
    workspace?.id ?? skipToken,
  );
  const { data: shopifyThemeProjectsUsage } =
    workspaceRouter.getShopifyThemeProjectsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const isWorkspaceShopifyBilled = workspace?.paymentProcessor === "shopify";

  const isCurrentTier = currentBillingTier === cardTier;
  const isYearlyIntervalUpgrade =
    activeSubscription?.billingInterval !== "yearly" && isYearly;
  const isManagedByReplo = activeSubscription?.isManagedByReplo ?? false;
  const isDowngrade = currentBillingTier === "pro" && cardTier === "starter";
  const isPlanCancelledButNotYetEnded =
    activeSubscription?.endedAt != null &&
    activeSubscription.endedAt > new Date();

  const currentAdditionalSites = activeSubscription
    ? getCountOfAdditionalSitesInSubscription(activeSubscription)
    : 0;
  const currentAdditionalProjects = activeSubscription
    ? getCountOfAdditionalProjectsInSubscription(activeSubscription)
    : 0;

  const currentAdditionalPublishedElementBundles = activeSubscription
    ? getCountOfAdditionalPublishedElementBundlesInSubscription(
        activeSubscription,
      )
    : 0;

  const hasConfigurationChanges =
    additionalSites !== currentAdditionalSites ||
    additionalProjects !== currentAdditionalProjects ||
    additionalPublishedElementBundles !==
      currentAdditionalPublishedElementBundles ||
    isYearlyIntervalUpgrade;

  const planInfo = getBillingPlanInfoByTier(cardTier);

  const totalPublishedElements =
    cardTier === "starter"
      ? planInfo.maxPublishedElements! +
        additionalPublishedElementBundles *
          ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE
      : null;

  // NOTE (Andrew, 2026-01-30): Pro plan has unlimited published items, so the
  // savings percentage calculation should not be affected by the Starter plan's
  // published item bundles slider. Always pass 0 for Pro plan.
  const publishedElementBundlesForSavings =
    cardTier === "pro" ? 0 : additionalPublishedElementBundles;
  const percentSavedAddOns = AddOn.buildFromCounts({
    additionalSites,
    additionalProjects,
    additionalPublishedElementBundles: publishedElementBundlesForSavings,
  });

  const percentSavedBySwitchingToYearly = getPercentSavedBySwitchingToYearly({
    billingTier: cardTier,
    addOns: percentSavedAddOns,
  });

  const selectedAddOns = AddOn.buildFromCounts({
    additionalSites,
    additionalProjects,
    additionalPublishedElementBundles,
  });

  // New UI (sites/projects)
  const sitesIncludedInBasePlan =
    workspace?.paymentProcessor === "stripe" ? 1 : 0;
  const projectsIncludedInBasePlan = 1;
  const totalSitesAllowedUnderProposedPlan =
    sitesIncludedInBasePlan + additionalSites;
  const totalProjectsAllowedUnderProposedPlan =
    projectsIncludedInBasePlan + additionalProjects;

  // NOTE (Cole 2025-12-09): Must check sites/projects and published elements usage
  const isPlanConfigurationValidWithTooltipContent = React.useMemo(() => {
    if (
      activeSubscription?.isManagedByReplo ||
      !reploSitesUsage ||
      !shopifyThemeProjectsUsage ||
      !publishedElementsUsage
    ) {
      return {
        isPlanConfigurationValid: true,
        tooltipContent: undefined,
      };
    }

    const currentReploHostedShops = reploSitesUsage.current;
    const currentShopifyHostedShops = shopifyThemeProjectsUsage.current;
    const { current: currentPublishedElements } = publishedElementsUsage;

    const publishedElementsAllowedUnderProposedPlan =
      planInfo.maxPublishedElements === null
        ? null
        : planInfo.maxPublishedElements +
          additionalPublishedElementBundles *
            ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE;

    if (currentReploHostedShops > totalSitesAllowedUnderProposedPlan) {
      return {
        isPlanConfigurationValid: false,
        tooltipContent: `Your workspace is using ${currentReploHostedShops} ${reploSitesTitle} ${pluralize({ singular: siteLabels.singular, plural: siteLabels.plural, count: currentReploHostedShops })}, but this plan configuration only allows ${totalSitesAllowedUnderProposedPlan}. You'll need to add more ${siteLabels.plural} to continue.`,
      };
    } else if (
      currentShopifyHostedShops > totalProjectsAllowedUnderProposedPlan
    ) {
      return {
        isPlanConfigurationValid: false,
        tooltipContent: `Your workspace is using ${currentShopifyHostedShops} ${shopifyThemeBuilderTitle} ${pluralize({ singular: projectLabels.singular, plural: projectLabels.plural, count: currentShopifyHostedShops })}, but this plan configuration only allows ${totalProjectsAllowedUnderProposedPlan}. You'll need to add more ${projectLabels.plural} to continue.`,
      };
    }

    if (
      publishedElementsAllowedUnderProposedPlan !== null &&
      currentPublishedElements > publishedElementsAllowedUnderProposedPlan
    ) {
      return {
        isPlanConfigurationValid: false,
        tooltipContent: `You have ${currentPublishedElements} published items, but your current plan configuration only allows ${publishedElementsAllowedUnderProposedPlan}. You'll need to add more published items to continue.`,
      };
    }

    return {
      isPlanConfigurationValid: true,
      tooltipContent: undefined,
    };
  }, [
    reploSitesUsage,
    shopifyThemeProjectsUsage,
    publishedElementsUsage,
    additionalPublishedElementBundles,
    planInfo.maxPublishedElements,
    totalSitesAllowedUnderProposedPlan,
    totalProjectsAllowedUnderProposedPlan,
    siteLabels,
    projectLabels,
    reploSitesTitle,
    shopifyThemeBuilderTitle,
    activeSubscription?.isManagedByReplo,
  ]);

  const {
    isPlanConfigurationValid,
    tooltipContent: invalidPlanConfigurationTooltipContent,
  } = isPlanConfigurationValidWithTooltipContent;

  return (
    <>
      <div
        className={twMerge(
          "flex flex-col flex-1 bg-sheet",
          position === "left" ? "rounded-l-xl" : "rounded-r-xl",
        )}
      >
        <div className="sticky top-0 z-10 bg-white">
          <div
            className={twMerge(
              "bg-sheet flex flex-col gap-4 p-5 border border-b-0.5 border-border",
              position === "left"
                ? "rounded-tl-xl border-r-0"
                : "rounded-tr-xl",
            )}
          >
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <PlanLabel tier={cardTier} />
                {cardTier === "starter" && <MostPopular />}
              </div>
              <span className="text-base text-muted">
                {cardTier === "starter"
                  ? "Best for entrepreneurs and small teams."
                  : "Best for agencies and high-scale teams."}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <EstimatedPlanPricePerMonth
                billingTier={cardTier}
                billingInterval={isYearly ? "yearly" : "monthly"}
                addOns={selectedAddOns}
                variant="header"
              />
              {isYearly && (
                <Tag size="sm" variant="v2">
                  Saving {percentSavedBySwitchingToYearly}% annually
                </Tag>
              )}
            </div>
            <PlanCardButton
              isCurrentTier={isCurrentTier}
              hasConfigurationChanges={hasConfigurationChanges}
              isYearlyIntervalUpgrade={isYearlyIntervalUpgrade}
              isDowngrade={isDowngrade}
              currentPlan={currentBillingTier}
              handleSubmitPlanChanges={() => {
                setPlanChangeModalOpen(true);
              }}
              isManagedByReplo={isManagedByReplo}
              isPlanConfigurationValid={isPlanConfigurationValid}
              invalidPlanConfigurationTooltipContent={
                invalidPlanConfigurationTooltipContent
              }
              billingInterval={isYearly ? "yearly" : "monthly"}
              isPlanCancelledButNotYetEnded={isPlanCancelledButNotYetEnded}
            />
          </div>
        </div>
        <div
          className={twMerge(
            "flex flex-col gap-4 p-5 border border-t-0 border-border",
            position === "left" ? "rounded-bl-xl border-r-0" : "rounded-br-xl",
          )}
        >
          <PlanCardProduct
            icon={<General />}
            title="General"
            features={getPlanCardCopy("general", cardTier)}
          />
          {!isWorkspaceShopifyBilled && (
            <PlanCardProduct
              icon={<ReploBuilder />}
              title={reploSitesTitle}
              features={getPlanCardCopy("replo-builder", cardTier)}
              footer={
                <div className="border-t-0.5 border-border p-3">
                  <QuantityControl
                    value={additionalSites}
                    onIncrement={handleAdditionalSitesIncrement}
                    onDecrement={handleAdditionalSitesDecrement}
                    label={
                      <span>
                        {totalSitesAllowedUnderProposedPlan}{" "}
                        {pluralize({
                          singular: siteLabels.singular,
                          plural: siteLabels.plural,
                          count: totalSitesAllowedUnderProposedPlan,
                        })}
                      </span>
                    }
                    minValue={0}
                  />
                </div>
              }
            />
          )}
          <PlanCardProduct
            icon={<ShopifyThemeEditor />}
            title={shopifyThemeBuilderTitle}
            features={getPlanCardCopy("shopify-theme-editor", cardTier)}
            footer={
              <ShopifyThemeEditorFooter
                cardTier={cardTier}
                additionalProjects={additionalProjects}
                additionalPublishedElementBundles={
                  additionalPublishedElementBundles
                }
                totalProjectsAllowedUnderProposedPlan={
                  totalProjectsAllowedUnderProposedPlan
                }
                totalPublishedElements={totalPublishedElements}
                projectLabels={projectLabels}
                handleAdditionalProjectsIncrement={
                  handleAdditionalProjectsIncrement
                }
                handleAdditionalProjectsDecrement={
                  handleAdditionalProjectsDecrement
                }
                handleAdditionalPublishedElementBundlesIncrement={
                  handleAdditionalPublishedElementBundlesIncrement
                }
                handleAdditionalPublishedElementBundlesDecrement={
                  handleAdditionalPublishedElementBundlesDecrement
                }
              />
            }
          />
          <PlanCardProduct
            icon={<Analytics />}
            title="Analytics"
            features={getPlanCardCopy("analytics", cardTier)}
          />
          <PlanCardProduct
            icon={<ABTesting />}
            title="A/B Testing"
            features={getPlanCardCopy("ab-testing", cardTier)}
          />
        </div>
      </div>
      <PlanChangeConfirmationModal
        open={planChangeModalOpen}
        onOpenChange={setPlanChangeModalOpen}
        newTier={cardTier}
        newBillingInterval={isYearly ? "yearly" : "monthly"}
        onBillingIntervalChange={(interval) => {
          return onIsYearlyChange(interval === "yearly");
        }}
        addOns={selectedAddOns}
      />
    </>
  );
};

const PlanCardProduct = ({
  icon,
  title,
  features,
  footer,
}: {
  icon: React.ReactNode;
  title: string;
  features: React.ReactNode[];
  footer?: React.ReactNode;
}) => {
  return (
    <div className="rounded-xl border-0.5 border-border bg-white flex flex-col ">
      <div className="flex flex-col gap-2 p-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="header-h6">{title}</span>
        </div>
        <div className="flex flex-col ml-2 gap-3 py-1.5">
          {features.map((feature) => {
            return feature;
          })}
        </div>
      </div>
      {footer}
    </div>
  );
};

const ShopifyThemeEditorFooter = ({
  cardTier,
  additionalProjects,
  additionalPublishedElementBundles,
  totalProjectsAllowedUnderProposedPlan,
  totalPublishedElements,
  projectLabels,
  handleAdditionalProjectsIncrement,
  handleAdditionalProjectsDecrement,
  handleAdditionalPublishedElementBundlesIncrement,
  handleAdditionalPublishedElementBundlesDecrement,
}: {
  cardTier: "starter" | "pro";
  additionalProjects: number;
  additionalPublishedElementBundles: number;
  totalProjectsAllowedUnderProposedPlan: number;
  totalPublishedElements: number | null;
  projectLabels: ReturnType<typeof getProjectLabels>;
  handleAdditionalProjectsIncrement: () => void;
  handleAdditionalProjectsDecrement: () => void;
  handleAdditionalPublishedElementBundlesIncrement: () => void;
  handleAdditionalPublishedElementBundlesDecrement: () => void;
}) => {
  return (
    <div className="border-t-0.5 border-border p-3 flex flex-col gap-2">
      <QuantityControl
        value={additionalProjects}
        onIncrement={handleAdditionalProjectsIncrement}
        onDecrement={handleAdditionalProjectsDecrement}
        label={
          <span>
            {totalProjectsAllowedUnderProposedPlan}{" "}
            {pluralize({
              singular: projectLabels.singular,
              plural: projectLabels.plural,
              count: totalProjectsAllowedUnderProposedPlan,
            })}
          </span>
        }
        minValue={0}
      />
      {cardTier === "starter" ? (
        <QuantityControl
          value={additionalPublishedElementBundles}
          onIncrement={handleAdditionalPublishedElementBundlesIncrement}
          onDecrement={handleAdditionalPublishedElementBundlesDecrement}
          label={<span>{totalPublishedElements} published items</span>}
          minValue={0}
          maxValue={MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES}
        />
      ) : (
        <div className="flex items-center justify-center border border-border bg-white rounded-md px-2 h-8">
          <span className="text-xs">
            <span className="font-bold">Unlimited</span> published items
          </span>
        </div>
      )}
    </div>
  );
};

export default PlanCard;
